import { Send, Heart } from 'lucide-react';

export function AdminOverview({ stats, reportEmail, setReportEmail, reportSending, reportResult, sendAdminReport, cdtSending, cdtResult, triggerCarmaDesTages }) {
  if (!stats) return null;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Übersicht</h2>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-blue-400">{stats.totalUsers}</div>
          <div className="text-gray-400 text-sm">Registrierte User</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-green-400">{stats.totalCarma}</div>
          <div className="text-gray-400 text-sm">Gesendete Carma</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-yellow-400">{stats.totalInvites}</div>
          <div className="text-gray-400 text-sm">Einladungen</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-purple-400">{stats.totalFeedback}</div>
          <div className="text-gray-400 text-sm">Feedback</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-cyan-400">{stats.totalImpulses || 0}</div>
          <div className="text-gray-400 text-sm">Community-Impulse</div>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-orange-400">{stats.sharedLinks || 0}</div>
          <div className="text-gray-400 text-sm">Geteilte Links</div>
          <div className="text-xs text-gray-500 mt-1">{stats.sharedViewed || 0} angesehen · {stats.sharedClaimed || 0} eingelöst</div>
        </div>
        {(stats.blockedUsers > 0 || stats.contentStrikes > 0) && (
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="text-3xl font-bold text-red-400">{stats.blockedUsers || 0}</div>
            <div className="text-gray-400 text-sm">Gesperrt ({stats.contentStrikes || 0} Strikes)</div>
          </div>
        )}
      </div>

      {/* Latest Activity */}
      {(stats.latestCarmaAt || stats.latestUserAt) && (
        <div className="bg-gray-800 rounded-xl p-4">
          <h3 className="font-medium mb-3">Letzte Aktivität</h3>
          <div className="space-y-2 text-sm">
            {stats.latestCarmaAt && (
              <div className="flex justify-between">
                <span className="text-gray-400">Letztes Carma:</span>
                <span className="text-green-400">{new Date(stats.latestCarmaAt).toLocaleString('de-DE')}</span>
              </div>
            )}
            {stats.latestUserAt && (
              <div className="flex justify-between">
                <span className="text-gray-400">Letzte Registrierung:</span>
                <span className="text-blue-400">{new Date(stats.latestUserAt).toLocaleString('de-DE')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Daily Stats */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="font-medium mb-3">Letzte 30 Tage</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {stats.dailyStats?.filter(d => d.users > 0 || d.carma > 0).map((day) => (
            <div key={day.date} className="flex items-center justify-between text-sm">
              <span className="text-gray-400">{day.date}</span>
              <div className="flex gap-4">
                <span className="text-blue-400">{day.users} User</span>
                <span className="text-green-400">{day.carma} Carma</span>
              </div>
            </div>
          ))}
          {stats.dailyStats?.every(d => d.users === 0 && d.carma === 0) && (
            <div className="text-gray-500">Keine Aktivität in den letzten 30 Tagen</div>
          )}
        </div>
      </div>

      {/* Admin Report */}
      <div className="bg-gray-800 rounded-xl p-4" data-testid="admin-report-section">
        <h3 className="font-medium mb-3 flex items-center gap-2"><Send size={16} className="text-blue-400" /> Admin Report per E-Mail</h3>
        <p className="text-sm text-gray-400 mb-3">Sende Dir die aktuelle Wochenstatistik als formatierte E-Mail.</p>
        <div className="flex gap-2">
          <input
            type="email"
            value={reportEmail}
            onChange={e => setReportEmail(e.target.value)}
            placeholder="admin@beispiel.de"
            data-testid="report-email-input"
            className="flex-1 rounded-lg bg-gray-700 border border-gray-600 px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={sendAdminReport}
            disabled={reportSending || !reportEmail.includes('@')}
            data-testid="send-report-btn"
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Send size={14} />
            {reportSending ? 'Sende…' : 'Report senden'}
          </button>
        </div>
        {reportResult && (
          <div className={`mt-2 text-sm ${reportResult.type === 'success' ? 'text-green-400' : 'text-red-400'}`} data-testid="report-result">
            {reportResult.message}
          </div>
        )}
      </div>

      {/* Carma des Tages */}
      <div className="bg-gray-800 rounded-xl p-4" data-testid="carma-des-tages-section">
        <h3 className="font-medium mb-3 flex items-center gap-2"><Heart size={16} className="text-pink-400" /> Carma des Tages</h3>
        <p className="text-sm text-gray-400 mb-3">Sendet jedem User mit Push-Abo eine zufällige erhaltene Wertschätzung als Erinnerung. Läuft automatisch täglich um 09:00 UTC.</p>
        <button
          onClick={triggerCarmaDesTages}
          disabled={cdtSending}
          data-testid="trigger-carma-des-tages-btn"
          className="px-4 py-2 text-sm bg-pink-600 hover:bg-pink-700 rounded-lg text-white disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <Heart size={14} />
          {cdtSending ? 'Sende…' : 'Jetzt auslösen'}
        </button>
        {cdtResult && (
          <div className={`mt-2 text-sm ${cdtResult.type === 'success' ? 'text-green-400' : 'text-red-400'}`} data-testid="cdt-result">
            {cdtResult.message}
          </div>
        )}
      </div>
    </div>
  );
}
